import {Graphics, Polygon, Rectangle} from "pixi.js";
import {HardwareItem} from "../../../../../../api/Hardware";
import {lineBlueHeavy, lineBlueLight, lineRed} from "../../const";

// A category-driven hardware drawing: a generic picture of a product category (a rim exit
// device, a closer, a kick plate, ...) overlaid on the door or frame at the item's placement.
// Each glyph decides which items it draws (by category path) and which targets it can draw
// on; HardwareCrosshairThing asks the registry (see ./index.ts) for one per placed item and
// falls back to the lever / deadbolt / crosshair when none matches.
export type HardwareGlyph = {
    id: string;
    // which placement targets this glyph draws on. A placement on another target keeps the
    // default marker.
    targets: ("door" | "frame")[];
    matches(item: HardwareCategoryFields): boolean;
    // draw into `g` (already cleared) in the Thing's local coords and return the hit area.
    draw(g: Graphics, ctx: GlyphContext): Rectangle | Polygon;
};

export type HardwareCategoryFields = Pick<HardwareItem, "category" | "category2" | "category3">;

export type GlyphRect = {x0: number; y0: number; x1: number; y1: number};

export type GlyphContext = {
    // the item being drawn, for glyphs that vary by the exact category.
    item: HardwareCategoryFields;
    // the placement centre (the crosshair hub) in local coords.
    hub: {x: number; y: number};
    // the target rect the placement sits in: the door leaf, or the frame stick.
    target: "door" | "frame";
    rect: GlyphRect;
    // which edge of `rect` the hinges are on. null for a frame placement.
    hingeSide: "left" | "right" | null;
    selected: boolean;
    // the placement breaks a placement rule (see model/hardware/MdHardwareRules); draw in red.
    invalid: boolean;
};

// the stroke/fill colour + alpha a glyph draws with, matching the lever / deadbolt glyphs.
// A rule-breaking placement draws red at full strength so it stands out.
export function glyphStyle(selected: boolean, invalid: boolean = false): {color: number; alpha: number} {
    if(invalid) return {color: lineRed, alpha: 1};
    return {color: selected ? lineBlueLight : lineBlueHeavy, alpha: selected ? 1 : 0.5};
}

// whether any of the item's category path levels equals one of `names` (case-insensitive).
export function hasCategory(item: HardwareCategoryFields, ...names: string[]): boolean {
    const keys = names.map(n => n.toLowerCase());
    return [item.category, item.category2, item.category3]
        .some(c => !!c && keys.includes(c.toLowerCase()));
}

// the strike (latch) edge of the rect: the edge opposite the hinges. Falls back to the right
// edge when the hinge side is unknown.
export function strikeEdgeX(ctx: GlyphContext): {x: number; dir: 1 | -1} {
    if(ctx.hingeSide === "right") return {x: ctx.rect.x0, dir: 1};
    return {x: ctx.rect.x1, dir: -1};
}

// the hinge edge of the rect. Falls back to the left edge when the hinge side is unknown.
export function hingeEdgeX(ctx: GlyphContext): number {
    return ctx.hingeSide === "right" ? ctx.rect.x1 : ctx.rect.x0;
}
